import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { trackPhoneClick, trackWhatsAppClick } from "../utils/analytics";

const sections = [
  {
    title: "What Cookies Are",
    items: [
      "Cookies are small files stored on your device when you visit a website.",
      "They help websites remember choices, keep essential features working, and understand how visitors use the site.",
    ],
  },
  {
    title: "Necessary Cookies",
    items: [
      "Necessary cookies support core website functions such as security, page navigation, booking form operation, and remembering cookie consent choices.",
      "These cookies are required for the website to work correctly and are not used for optional analytics tracking.",
    ],
  },
  {
    title: "Analytics Cookies",
    items: [
      "Analytics cookies help us understand how visitors use the website, which pages are useful, and where website performance can be improved.",
      "Analytics cookies are used only when you accept analytics tracking through the cookie consent banner.",
    ],
  },
  {
    title: "Google Analytics and GA4",
    items: [
      "Limassol Airport Taxi may use Google Analytics 4 to measure website visits, page views, and general performance trends.",
      "GA4 data helps us improve the booking experience and understand demand for airport transfer routes in Cyprus.",
    ],
  },
  {
    title: "Cookie Consent Banner",
    items: [
      "The cookie consent banner lets you choose whether to allow analytics cookies when you visit the website.",
      "Necessary cookies may still be used even if you reject analytics cookies because they support basic website functionality.",
    ],
  },
  {
    title: "Accepting or Rejecting Analytics Cookies",
    items: [
      "You can accept analytics cookies from the banner to allow website performance tracking.",
      "You can reject analytics cookies from the banner to prevent optional analytics tracking from being enabled.",
    ],
  },
  {
    title: "Cookie Settings Footer Option",
    items: [
      "You can reopen cookie choices using the Cookie Settings option in the website footer.",
      "Using Cookie Settings lets you update your analytics cookie preference after your first visit.",
    ],
  },
  {
    title: "Managing Cookies in Your Browser",
    items: [
      "Most browsers let you block, delete, or manage cookies through browser settings.",
      "Blocking some cookies may affect parts of the website, including saved consent choices or booking-related functionality.",
    ],
  },
];

function CookiePolicyPage() {
  return (
    <main className="min-h-screen bg-[#030712] px-6 py-16 text-white">
      <Helmet>
        <title>Cookie Policy | Limassol Airport Taxi</title>
        <meta
          name="description"
          content="Cookie Policy for Limassol Airport Taxi, including analytics cookie consent and website performance tracking."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Cookie Policy | Limassol Airport Taxi" />
        <meta
          property="og:description"
          content="Cookie Policy for Limassol Airport Taxi, including analytics cookie consent and website performance tracking."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://limassolairporttaxi.com/cookie-policy" />
        <link rel="canonical" href="https://limassolairporttaxi.com/cookie-policy" />
      </Helmet>

      <section className="mx-auto max-w-4xl">
        <Link
          to="/"
          className="inline-flex rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-sm font-bold text-white transition hover:border-yellow-400/50 hover:text-yellow-300"
        >
          Back to Home
        </Link>

        <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/30 sm:p-10">
          <p className="text-sm font-bold uppercase tracking-wide text-yellow-400">
            Limassol Airport Taxi
          </p>

          <h1 className="mt-3 text-4xl font-black text-yellow-400 sm:text-5xl">
            Cookie Policy
          </h1>

          <p className="mt-5 leading-8 text-neutral-300">
            This Cookie Policy explains how Limassol Airport Taxi uses necessary
            and analytics cookies, including Google Analytics 4, and how you can
            manage your cookie choices on this website.
          </p>
        </div>

        <div className="mt-8 grid gap-5">
          {sections.map((section) => (
            <section
              key={section.title}
              className="rounded-3xl border border-white/10 bg-black/30 p-6 sm:p-8"
            >
              <h2 className="text-2xl font-bold text-yellow-400">
                {section.title}
              </h2>

              <ul className="mt-4 space-y-3 text-neutral-300">
                {section.items.map((item) => (
                  <li key={item} className="flex gap-3 leading-7">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-yellow-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section className="mt-8 rounded-3xl border border-yellow-400/20 bg-yellow-400/10 p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-yellow-400">Contact</h2>

          <p className="mt-4 leading-8 text-neutral-200">
            For questions about this Cookie Policy or website analytics consent,
            contact Limassol Airport Taxi using the phone or WhatsApp details on
            this website.
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a
              href="tel:+35796613191"
              onClick={() => trackPhoneClick("cookie_policy_phone")}
              className="rounded-2xl bg-yellow-400 px-5 py-3 text-center font-bold text-black transition hover:bg-yellow-300"
            >
              Call +357 96 613 191
            </a>

            <a
              href="https://wa.me/35796613191?text=Hello,%20I%20have%20a%20question%20about%20your%20cookie%20policy."
              onClick={() => trackWhatsAppClick("cookie_policy_whatsapp")}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-center font-bold text-white transition hover:border-yellow-400/50 hover:text-yellow-300"
            >
              WhatsApp
            </a>
          </div>
        </section>
      </section>
    </main>
  );
}

export default CookiePolicyPage;
